import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, Camera, Images, Instagram } from 'lucide-react';
import { buildLocalePath, type Locale } from '@/i18n/config';
import { cn } from '@/lib/utils';
import { DGD_INSTAGRAM_URL } from '../lib/constants';

interface RideDayCTACardsProps {
  locale: Locale;
  /** Light matches the Live page cream/gold-tint surfaces; dark sits on DGD navy. */
  tone?: 'light' | 'dark';
  /** Optional attribute name applied to each card link for scroll reveal hooks. */
  revealAttr?: string;
  /** Render the "Stay connected" intro above the cards. Off when the host section has its own. */
  showHeading?: boolean;
  className?: string;
}

export function RideDayCTACards({
  locale,
  tone = 'light',
  revealAttr,
  showHeading = true,
  className,
}: RideDayCTACardsProps) {
  const isDark = tone === 'dark';
  const revealProps = revealAttr ? { [revealAttr]: '' } : {};
  const cards = [
    {
      href: DGD_INSTAGRAM_URL,
      external: true,
      bg: '/images/dgd/DGD_HeroImage.jpg',
      icon: <Instagram className="h-5 w-5" strokeWidth={1.8} />,
      eyebrow: 'Live on Instagram',
      title: 'Follow the drive live',
      desc: 'Watch drivers roll out across the world in real time.',
      cta: 'Watch on Instagram',
    },
    {
      href: buildLocalePath(locale, '/content-upload'),
      external: false,
      bg: '/images/dgd/DGD_HeroImage2.jpg',
      icon: <Camera className="h-5 w-5" strokeWidth={1.8} />,
      eyebrow: 'After the drive',
      title: 'Upload your photos',
      desc: 'Add your photos to the global gallery and share your drive.',
      cta: 'Upload photos',
    },
    {
      href: buildLocalePath(locale, '/gallery'),
      external: false,
      bg: '/images/dgd/DGD26_SITEHEADER.jpg',
      icon: <Images className="h-5 w-5" strokeWidth={1.8} />,
      eyebrow: 'Live gallery',
      title: 'See the world drive',
      desc: 'Photos pour in live from every city. Watch the gallery fill in real time.',
      cta: 'Open gallery',
    },
  ];

  return (
    <div className={cn('mt-20 md:mt-28', className)}>
      {showHeading && (
        <div className="mx-auto max-w-3xl text-center">
          <p
            {...revealProps}
            className={cn(
              'text-xs font-medium uppercase tracking-[0.3em]',
              isDark ? 'text-dgd-gold' : 'text-dgd-gold-hover',
            )}
          >
            Stay connected
          </p>
          <h2
            {...revealProps}
            className={cn(
              'mt-3 text-2xl font-bold md:text-3xl',
              isDark ? 'text-dgd-cream' : 'text-stone-950',
            )}
          >
            Watch, share, relive
          </h2>
          <p
            {...revealProps}
            className={cn(
              'mt-3 text-sm md:text-base',
              isDark ? 'text-dgd-cream/75' : 'text-stone-700',
            )}
          >
            Follow the drive live, upload your photos, and explore the global gallery.
          </p>
        </div>
      )}
      <div className={cn('grid grid-cols-1 gap-6 md:grid-cols-3', showHeading && 'mt-10')}>
        {cards.map((card) => {
          const inner = (
            <div className="relative h-[26rem] overflow-hidden rounded-2xl border border-white/10 bg-stone-900 shadow-2xl shadow-black/40 transition-transform duration-500 ease-out group-hover:-translate-y-2 group-hover:border-dgd-gold/40 group-hover:shadow-dgd-gold/10">
              <div className="absolute inset-0">
                <Image
                  src={card.bg}
                  alt=""
                  fill
                  sizes="(min-width: 768px) 33vw, 100vw"
                  className="object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/70 to-stone-950/10 transition-opacity duration-500 group-hover:opacity-90" />
              <div className="absolute inset-0 bg-stone-950/30 transition-opacity duration-500 group-hover:bg-stone-950/10" />
              <div className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur-md transition-all duration-500 group-hover:border-dgd-gold group-hover:bg-dgd-gold group-hover:text-stone-950">
                {card.icon}
              </div>
              <div className="absolute inset-x-0 bottom-0 p-7 md:p-8">
                <p className="text-[11px] uppercase tracking-[0.25em] text-dgd-gold">
                  {card.eyebrow}
                </p>
                <h3 className="mt-3 text-2xl font-bold leading-tight text-white md:text-[28px]">
                  {card.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-300/90">{card.desc}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white transition-colors group-hover:text-dgd-gold">
                  <span>{card.cta}</span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-dgd-gold transition-all duration-500 group-hover:w-full" />
            </div>
          );
          return card.external ? (
            <a
              key={card.title}
              {...revealProps}
              href={card.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
            >
              {inner}
            </a>
          ) : (
            <Link
              key={card.title}
              {...revealProps}
              href={card.href}
              prefetch={false}
              className="group block"
            >
              {inner}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
